import { Link } from 'react-router-dom'
import NavBar from '../components/layout/NavBar'
import { getToken } from '../lib/auth'
import { useMe } from '../hooks/useMe'

export default function PeopleEdit() {
  const authed = Boolean(getToken())
  const { me } = useMe(authed)

  const displayName = me?.profile?.display_name || me?.username || 'Your People Pixe'
  const handle = me?.profile?.handle ? `@${me.profile.handle}` : ''
  const avatar = me?.profile?.avatar_url || ''
  const cover = (me as any)?.profile?.cover_url || ''

  const tasks: Array<{ key: string; label: string; done: boolean; href: string }> = [
    { key: 'bio', label: 'Write your bio', done: Boolean((me as any)?.profile?.about_short), href: '/people/edit/new' },
    { key: 'cover', label: 'Add a Pixe image', done: Boolean(cover), href: '/people/edit/new' },
    { key: 'links', label: 'Add your links', done: Boolean(me?.profile?.links && me.profile.links.length > 0), href: '/settings' },
    { key: 'avatar', label: 'Set your avatar', done: Boolean(avatar), href: '/settings' },
    { key: 'handle', label: 'Choose your handle', done: Boolean(me?.profile?.handle), href: '/settings' },
  ]
  const done = tasks.filter(t => t.done).length
  const total = tasks.length
  const pct = Math.round((done / Math.max(1, total)) * 100)

  return (
    <div className="min-h-screen">
      <NavBar showSearch={false} />
      <main className="mx-auto max-w-7xl px-4 py-10">
        {/* Hero (personalized) */}
        <section className="relative overflow-hidden rounded-2xl border border-white/10">
          <div className="relative">
            <div className="relative aspect-[16/6] md:aspect-[16/5]">
              {cover ? (
                <img src={cover} alt="Cover" className="absolute inset-0 h-full w-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-[radial-gradient(80%_80%_at_10%_-10%,rgba(99,102,241,0.25),transparent_60%)]" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 via-neutral-950/30 to-transparent" />
            </div>
            <div className="absolute inset-0 flex items-end">
              <div className="p-6 md:p-8 w-full">
                <div className="flex items-center gap-3">
                  {avatar ? (
                    <img src={avatar} className="h-14 w-14 rounded-full object-cover border border-white/20" />
                  ) : (
                    <div className="h-14 w-14 rounded-full bg-white/10" />
                  )}
                  <div>
                    <div className="text-2xl font-extrabold tracking-tight">{displayName}</div>
                    <div className="text-white/80 text-sm">{handle}</div>
                  </div>
                </div>
                <p className="mt-3 text-white/85 max-w-2xl">
                  Introduce yourself with a beautiful, scannable profile. Write a bio powered by AI, add a standout Pixe image, and guide people to what’s next.
                </p>
                <div className="mt-4 flex flex-wrap gap-3">
                  <Link to={authed ? '/people/edit/new' : '/login'} className="inline-flex items-center rounded-xl bg-brand px-5 py-3 font-semibold shadow hover:bg-brand-600">
                    {authed ? 'Open Editor' : 'Sign in to edit'}
                  </Link>
                  <a href="#why" className="inline-flex items-center rounded-xl border border-white/15 px-5 py-3 font-semibold hover:bg-white/5">Learn more</a>
                </div>
              </div>
            </div>
          </div>
          <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 text-sm text-white/80 p-6 md:p-8 border-t border-white/10 bg-neutral-950/40">
            <li className="glass rounded-lg p-3">{(me as any)?.profile?.about_short ? 'Polished bio added' : 'Write your story with AI help'}</li>
            <li className="glass rounded-lg p-3">{cover ? 'Hero cover set' : 'Add a Pixe image for your header'}</li>
            <li className="glass rounded-lg p-3">{me?.profile?.links?.length ? 'Links ready to go' : 'Add links to your best work'}</li>
            <li className="glass rounded-lg p-3">Looks great on web and mobile</li>
          </ul>
        </section>

        {/* Progress + Why / How */}
        <section className="mt-10">
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-extrabold">Your setup progress</h2>
              <div className="text-sm text-white/80">{done}/{total} complete</div>
            </div>
            <div className="mt-2 h-2 rounded-full bg-white/10 overflow-hidden">
              <div className="h-full bg-brand" style={{ width: `${pct}%` }} />
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {tasks.map((t) => (
              <Link key={t.key} to={t.href} className={`rounded-xl border p-4 flex items-center justify-between ${t.done ? 'border-emerald-500/30 bg-emerald-500/10' : 'border-white/10 bg-white/5 hover:bg-white/10'}`}>
                <div className="flex items-center gap-3">
                  <span className={`inline-flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${t.done ? 'bg-emerald-500 text-white' : 'bg-white/10 text-white/80'}`}>{t.done ? '✓' : '•'}</span>
                  <div className="font-semibold">{t.label}</div>
                </div>
                <div className="text-xs text-white/70">{t.done ? 'Done' : 'Start'}</div>
              </Link>
            ))}
          </div>
        </section>

        {/* Why / How */}
        <section id="why" className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
          <InfoCard title="Tell your story">
            Use headings, lists, and quotes to share what you do, what you love, and how people can reach you.
          </InfoCard>
          <InfoCard title="AI that helps">
            Start from a prompt or ask AI to tighten tone, summarize, or expand your bio — you’re always in control.
          </InfoCard>
          <InfoCard title="Look great anywhere">
            Add a Pixe image for your header. Your name, handle, and bio render cleanly on web and mobile.
          </InfoCard>
        </section>

        {/* CTA */}
        <section className="mt-10">
          <div className="rounded-2xl border border-white/10 p-6 flex items-center justify-between">
            <div>
              <div className="text-white font-extrabold">Ready to edit your People Pixe?</div>
              <div className="text-white/80">Open the editor to write your bio, add your Pixe image, and preview changes live.</div>
            </div>
            <Link to={authed ? '/people/edit/new' : '/login'} className="inline-flex items-center rounded-xl bg-brand px-4 py-2 font-semibold shadow hover:bg-brand-600">
              {authed ? 'Open Editor' : 'Sign in'}
            </Link>
          </div>
        </section>
      </main>
    </div>
  )
}

function InfoCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-2xl border border-white/10 p-4">
      <div className="text-white font-semibold mb-1">{title}</div>
      <div className="text-white/80">{children}</div>
    </div>
  )
}
